import type { ReactElement } from 'react'
import React from 'react'
import { Text, View } from 'react-native'
import { tv } from 'tailwind-variants'

import { useFlowsServicesResponses } from '@flows-ui/services/FlowServicesResponsesContext'
import { useTranslation } from '@flows-ui/utils/i18n'
import { processStepContentElementText } from '@flows-ui/utils/processStepContentElementText'
import { EVENTS } from '@flows/configs/analytics/constants'
import type { Subtitle, Title as TitleProps } from '@flows/core/types/stepContentTypes'
import type { FlowValues } from '@flows/core/types/stepTypes'
import InfoButton from '@ui-kit/components/InfoButton'
import * as typography from '@ui-kit/core/typography'

export const titleStyles = tv({
  slots: {
    container: '',
    textContainer: '',
  },
  variants: {
    hasModalContent: {
      true: {
        container: 'flex flex-row items-center justify-between gap-x-2.5',
        textContainer: 'flex-1',
      },
    },
  },
})

type TrackFn = (event: string, properties?: Record<string, unknown>) => void

export const Title = <TProps extends TitleProps<any, any, any> | Subtitle<any, any, any>>(
  props: TProps & {
    flowValues: FlowValues
    isSubtitle?: boolean
    modalContent?: ReactElement
    track?: TrackFn
  }
) => {
  const { t } = useTranslation()
  const servicesResponses = useFlowsServicesResponses()
  const translateFunction = processStepContentElementText(t, {
    flowValues: props.flowValues,
    servicesResponses,
  })
  const style = props.isSubtitle ? typography[props.size ?? 'h5'] : typography[props.size ?? 'h4']

  if (!props.text) {
    return null
  }
  const title = translateFunction(props.text)
  const popupId =
    props.text && typeof props.text === 'object' && 'translationKey' in props.text
      ? `info_${props.text.translationKey}`
      : `info_${title}`

  const { container, textContainer } = titleStyles({
    hasModalContent: Boolean(props.modalContent),
  })
  const textClassName =
    'isQuestion' in props && props.isQuestion
      ? typography.body1({ weight: 'semibold', align: props.align, className: textContainer() })
      : style({ className: textContainer(), type: props.fontType, align: props.align })
  return (
    <View className={container()}>
      <Text className={textClassName}>{title}</Text>
      {props.modalContent ? (
        <InfoButton
          type="info"
          modalContent={props.modalContent}
          backdrop={props.infoButton?.backdrop}
          onOpen={() => props.track?.(EVENTS.PopupOpened, { id: popupId })}
        />
      ) : null}
    </View>
  )
}
